import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { updatePassword } from 'aws-amplify/auth';
import { updateUserAttribute, confirmUserAttribute, type UpdateUserAttributeOutput } from 'aws-amplify/auth';
import { useDomainStore, useActivityStore } from '@/hooks/useStores';
import ActivityListItem from '@/components/activity/ActivityListItem';
import NotificationPreferencesForm from './NotificationPreferencesForm';
import LanguageSelector from '@/components/language/LanguageSelector';
import { getSnapshot } from 'mobx-state-tree';
import type { ActivityEvent } from '@/types/activity';
import api from '@/api';
import StickyTabStrip from '@/components/shared/StickyTabStrip';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import PageTourIntro from '@/components/help/PageTourIntro';
import ScreenTourLayer from '@/components/help/ScreenTourLayer';
import { useScreenTour } from '@/hooks/useScreenTour';
import { PROFILE_ACCOUNT_TOUR_STEPS, PROFILE_ACCOUNT_TOUR_STORAGE_KEY } from '@/data/profile-account-tour';
import { userIsSocialOnlyAccount } from '@/utils/cognitoIdentities';
import './profile.css';

type AccountTab = 'password' | 'profile' | 'email' | 'activity' | 'notifications';

const ACCOUNT_TAB_IDS: AccountTab[] = ['profile', 'password', 'email', 'activity', 'notifications'];

function parseAccountTab(value: string | null): AccountTab {
  return value && ACCOUNT_TAB_IDS.includes(value as AccountTab) ? (value as AccountTab) : 'profile';
}

const ProfileScreen: React.FC = observer(() => {
  const { t } = useTranslation('profile');
  const { t: tCommon } = useTranslation('common');
  const { t: tLanguage } = useTranslation('language');
  const domainStore = useDomainStore();
  const activityStore = useActivityStore();
  const user = domainStore.user;
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFederatedSignIn, setIsFederatedSignIn] = useState(false);

  const accountTabs = useMemo(
    () =>
      ACCOUNT_TAB_IDS.map((id) => ({
        id,
        label: t(`tabs.${id}`)
      })),
    [t]
  );

  const activeTab = parseAccountTab(searchParams.get('tab'));

  const setActiveTab = useCallback(
    (tab: AccountTab) => {
      setSearchParams(tab === 'profile' ? {} : { tab }, { replace: true });
    },
    [setSearchParams]
  );

  const accountTour = useScreenTour({
    steps: PROFILE_ACCOUNT_TOUR_STEPS,
    completedStorageKey: PROFILE_ACCOUNT_TOUR_STORAGE_KEY
  });

  const startAccountTour = () => {
    setActiveTab('profile');
    accountTour.startTour();
  };

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Nickname form
  const [nickname, setNickname] = useState(user?.nickname ?? '');
  const [nicknameMessage, setNicknameMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [nicknameLoading, setNicknameLoading] = useState(false);

  // Email form
  const [email, setEmail] = useState(user?.email ?? '');
  const [emailStep, setEmailStep] = useState<'input' | 'confirm'>('input');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [emailMessage, setEmailMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);

  const [notificationsDirty, setNotificationsDirty] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsMessage, setNotificationsMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  useEffect(() => {
    if (user) {
      setNickname(user.nickname ?? '');
      setEmail(user.email ?? '');
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    void userIsSocialOnlyAccount().then((socialOnly) => {
      if (!cancelled) setIsFederatedSignIn(socialOnly);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    setPasswordMessage(null);
    setNicknameMessage(null);
    setEmailMessage(null);
    setNotificationsMessage(null);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'activity') {
      void activityStore.loadAccountActivity({ pageSize: 10 });
    }
  }, [activeTab, activityStore]);

  const accountActivityItems = activityStore.accountItems.map((item) => getSnapshot(item) as ActivityEvent);

  useEffect(() => {
    if (activeTab !== 'email' && emailStep === 'confirm') {
      setEmailStep('input');
      setConfirmationCode('');
      setEmail(user?.email ?? '');
    }
  }, [activeTab, emailStep, user?.email]);

  const savedNickname = user?.nickname ?? '';
  const savedEmail = user?.email ?? '';

  const passwordDirty = currentPassword.length > 0 && newPassword.length > 0 && confirmPassword.length > 0;
  const profileDirty = nickname.trim() !== savedNickname.trim();
  const emailDirty =
    emailStep === 'confirm'
      ? confirmationCode.trim().length > 0
      : email.trim() !== savedEmail.trim() && email.trim().length > 0;

  const activePanelTitle = useMemo(
    () => accountTabs.find((tab) => tab.id === activeTab)?.label ?? t('title'),
    [accountTabs, activeTab, t]
  );

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: t('password.mismatch') });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: t('password.minLength') });
      return;
    }
    setPasswordLoading(true);
    try {
      await updatePassword({
        oldPassword: currentPassword,
        newPassword
      });
      setPasswordMessage({ type: 'success', text: t('password.updated') });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('password.updateFailed');
      setPasswordMessage({ type: 'error', text: msg });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleNicknameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNicknameMessage(null);
    const trimmed = nickname.trim();
    if (!trimmed) {
      setNicknameMessage({ type: 'error', text: t('profileTab.nicknameRequired') });
      return;
    }
    setNicknameLoading(true);
    try {
      await updateUserAttribute({
        userAttribute: {
          attributeKey: 'nickname',
          value: trimmed
        }
      });
      const result = await api.users.updateProfile({ nickname: trimmed });
      if (result.success) {
        domainStore.updateUserProfile({ nickname: trimmed });
        setNicknameMessage({ type: 'success', text: t('profileTab.updated') });
      } else {
        setNicknameMessage({ type: 'error', text: result.error.getUserMessage() });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('profileTab.updateFailed');
      setNicknameMessage({ type: 'error', text: msg });
    } finally {
      setNicknameLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailMessage(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailMessage({ type: 'error', text: t('email.required') });
      return;
    }
    if (emailStep === 'input') {
      setEmailLoading(true);
      try {
        const output: UpdateUserAttributeOutput = await updateUserAttribute({
          userAttribute: {
            attributeKey: 'email',
            value: trimmed
          }
        });
        if (output.nextStep?.updateAttributeStep === 'CONFIRM_ATTRIBUTE_WITH_CODE') {
          setEmailStep('confirm');
          setEmailMessage({
            type: 'success',
            text: t('email.verificationSent', { email: trimmed })
          });
        } else {
          const result = await api.users.updateProfile({ email: trimmed });
          if (result.success) {
            domainStore.updateUserProfile({ email: trimmed });
            setEmailMessage({ type: 'success', text: t('email.updated') });
          } else {
            setEmailMessage({ type: 'error', text: result.error.getUserMessage() });
          }
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : t('email.updateFailed');
        setEmailMessage({ type: 'error', text: msg });
      } finally {
        setEmailLoading(false);
      }
    } else {
      setEmailLoading(true);
      try {
        await confirmUserAttribute({
          userAttributeKey: 'email',
          confirmationCode: confirmationCode.trim()
        });
        const result = await api.users.updateProfile({ email: trimmed });
        if (result.success) {
          domainStore.updateUserProfile({ email: trimmed });
          setEmailMessage({ type: 'success', text: t('email.updated') });
          setEmailStep('input');
          setConfirmationCode('');
        } else {
          setEmailMessage({ type: 'error', text: result.error.getUserMessage() });
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : t('email.verificationFailed');
        setEmailMessage({ type: 'error', text: msg });
      } finally {
        setEmailLoading(false);
      }
    }
  };

  const resetEmailConfirmation = () => {
    setEmailStep('input');
    setConfirmationCode('');
    setEmailMessage(null);
    setEmail(user?.email ?? '');
  };

  const renderPanelActions = (formId: string, dirty: boolean, loading: boolean, extra?: React.ReactNode) => (
    <div className="profile-page__panel-actions">
      {extra}
      <button type="submit" form={formId} className="btn btn--primary" disabled={!dirty || loading}>
        {loading ? tCommon('saving') : tCommon('save')}
      </button>
    </div>
  );

  return (
    <div className="profile-page">
      <header className="profile-page__header" data-tour="account-header">
        <PageTourIntro
          className="profile-page__tour-intro"
          onStartTour={startAccountTour}
          heading={<h1 className="profile-page__title">{t('title')}</h1>}
        >
          <p className="profile-page__lede" data-tour="account-intro">
            {t('lede')} <ScreenHelpDot />
          </p>
        </PageTourIntro>
      </header>

      <div data-tour="account-tabs">
        <StickyTabStrip
          className="profile-page__tabs"
          ariaLabel={t('tabsAriaLabel')}
          tabs={accountTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      <div
        className="profile-page__panel"
        role="tabpanel"
        id={`panel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
        data-tour="account-panel"
      >
        <div className="tab-panel-card__head">
          <h2 className="tab-panel-card__title">{activePanelTitle}</h2>
          {activeTab === 'password' &&
            !isFederatedSignIn &&
            renderPanelActions('account-password-form', passwordDirty, passwordLoading)}
          {activeTab === 'profile' && renderPanelActions('account-profile-form', profileDirty, nicknameLoading)}
          {activeTab === 'notifications' &&
            renderPanelActions('account-notifications-form', notificationsDirty, notificationsLoading)}
          {activeTab === 'email' &&
            !isFederatedSignIn &&
            renderPanelActions(
              'account-email-form',
              emailDirty,
              emailLoading,
              emailStep === 'confirm' ? (
                <button type="button" className="btn btn--ghost" onClick={resetEmailConfirmation}>
                  {tCommon('cancel')}
                </button>
              ) : undefined
            )}
        </div>

        <div className="tab-panel-card__body">
          {activeTab === 'password' &&
            (isFederatedSignIn ? (
              <div className="profile-page__form">
                <p className="profile-page__federated-note">{t('password.federatedNote')}</p>
              </div>
            ) : (
              <form id="account-password-form" onSubmit={handlePasswordSubmit} className="profile-page__form">
                <div className="profile-page__field">
                  <label htmlFor="current-password" className="profile-page__label">
                    {t('password.currentLabel')}
                  </label>
                  <input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="profile-page__input"
                    required
                    autoComplete="current-password"
                  />
                </div>
                <div className="profile-page__field">
                  <label htmlFor="new-password" className="profile-page__label">
                    {t('password.newLabel')}
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="profile-page__input"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
                <div className="profile-page__field">
                  <label htmlFor="confirm-password" className="profile-page__label">
                    {t('password.confirmLabel')}
                  </label>
                  <input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="profile-page__input"
                    required
                    autoComplete="new-password"
                  />
                </div>
                {passwordMessage && (
                  <div className={`profile-page__message profile-page__message--${passwordMessage.type}`} role="alert">
                    {passwordMessage.text}
                  </div>
                )}
              </form>
            ))}

          {activeTab === 'profile' && (
            <form id="account-profile-form" onSubmit={handleNicknameSubmit} className="profile-page__form">
              <div className="profile-page__field">
                <label htmlFor="nickname" className="profile-page__label">
                  {t('profileTab.displayNameLabel')}
                </label>
                <input
                  id="nickname"
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="profile-page__input"
                  placeholder={t('profileTab.displayNamePlaceholder')}
                  autoComplete="nickname"
                />
                <p className="profile-page__hint">{t('profileTab.displayNameHint')}</p>
              </div>
              <div className="profile-page__field" data-tour="account-language">
                <LanguageSelector id="profile-language" />
                <p className="profile-page__hint">{tLanguage('languageHelp')}</p>
              </div>
              {nicknameMessage && (
                <div className={`profile-page__message profile-page__message--${nicknameMessage.type}`} role="alert">
                  {nicknameMessage.text}
                </div>
              )}
            </form>
          )}

          {activeTab === 'email' &&
            (isFederatedSignIn ? (
              <div className="profile-page__form">
                <p className="profile-page__federated-note">
                  {t('email.federatedNote', {
                    email: savedEmail || t('email.providerAccountFallback')
                  })}
                </p>
              </div>
            ) : (
              <form id="account-email-form" onSubmit={handleEmailSubmit} className="profile-page__form">
                {emailStep === 'input' ? (
                  <div className="profile-page__field">
                    <label htmlFor="email" className="profile-page__label">
                      {t('email.addressLabel')}
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="profile-page__input"
                      placeholder={t('email.addressPlaceholder')}
                      required
                    />
                  </div>
                ) : (
                  <div className="profile-page__field">
                    <label htmlFor="confirmation-code" className="profile-page__label">
                      {t('email.verificationCodeLabel')}
                    </label>
                    <input
                      id="confirmation-code"
                      type="text"
                      value={confirmationCode}
                      onChange={(e) => setConfirmationCode(e.target.value)}
                      className="profile-page__input"
                      placeholder={t('email.verificationCodePlaceholder')}
                      required
                      autoComplete="one-time-code"
                    />
                    <p className="profile-page__hint">{t('email.verificationHint', { email: email.trim() })}</p>
                  </div>
                )}
                {emailMessage && (
                  <div className={`profile-page__message profile-page__message--${emailMessage.type}`} role="alert">
                    {emailMessage.text}
                  </div>
                )}
              </form>
            ))}

          {activeTab === 'activity' && (
            <div className="profile-page__activity">
              <p className="profile-page__hint">{t('activity.hint')}</p>
              {activityStore.isLoadingAccount ? (
                <p className="profile-page__activity-empty">{t('activity.loading')}</p>
              ) : accountActivityItems.length === 0 ? (
                <p className="profile-page__activity-empty">{t('activity.empty')}</p>
              ) : (
                <div className="profile-page__activity-list">
                  {accountActivityItems.map((event) => (
                    <ActivityListItem key={event.id} event={event} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'notifications' && (
            <>
              <NotificationPreferencesForm
                formId="account-notifications-form"
                onDirtyChange={setNotificationsDirty}
                onSavingChange={setNotificationsLoading}
                onMessage={setNotificationsMessage}
              />
              {notificationsMessage && (
                <div
                  className={`profile-page__message profile-page__message--${notificationsMessage.type}`}
                  role="alert"
                >
                  {notificationsMessage.text}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {activeTab === 'profile' && (
        <section className="profile-page__danger" aria-labelledby="profile-danger-heading" data-tour="account-danger">
          <h2 id="profile-danger-heading" className="profile-page__danger-title">
            {t('deleteAccount.title')}
          </h2>
          <p className="profile-page__danger-copy">{t('deleteAccount.copy')}</p>
          <Link to="/profile/delete-account" className="btn btn--ghost">
            {t('deleteAccount.button')}
          </Link>
        </section>
      )}

      <ScreenTourLayer tour={accountTour} />
    </div>
  );
});

export default ProfileScreen;
